import { useEffect, useRef, useState, type ReactNode } from 'react';
import { usePreferences } from '../hooks/usePreferences';
import {
  IconBook,
  IconChevron,
  IconCopy,
  IconGift,
  IconHelp,
  IconHome,
  IconKey,
  IconLogout,
  IconPayment,
  IconSettings,
  IconShield,
  IconSubscription,
  IconUser,
} from './icons';

export type CabinetNav = 'home' | 'profile' | 'help' | 'support' | 'settings';
export type ProfileTab = 'subscription' | 'vpn' | 'payments' | 'promo';

const NAV_ITEMS: { id: CabinetNav; label: string; Icon: typeof IconHome }[] = [
  { id: 'home', label: 'Главная', Icon: IconHome },
  { id: 'profile', label: 'Профиль', Icon: IconUser },
  { id: 'help', label: 'Инструкции', Icon: IconBook },
  { id: 'support', label: 'Поддержка', Icon: IconHelp },
];

const NAV_TITLES: Record<CabinetNav, string> = {
  home: 'Главная',
  profile: 'Профиль',
  help: 'Инструкции',
  support: 'Поддержка',
  settings: 'Настройки',
};

export const PROFILE_TABS: { id: ProfileTab; label: string; Icon: typeof IconHome }[] = [
  { id: 'subscription', label: 'Подписка', Icon: IconSubscription },
  { id: 'vpn', label: 'Ключи', Icon: IconKey },
  { id: 'payments', label: 'Оплата', Icon: IconPayment },
  { id: 'promo', label: 'Промо', Icon: IconGift },
];

interface CabinetLayoutProps {
  activeNav: CabinetNav;
  onNavChange: (nav: CabinetNav) => void;
  profileTab: ProfileTab;
  onProfileTabChange: (tab: ProfileTab) => void;
  userName: string;
  userId: number;
  userTierLabel?: string | null;
  botUrl?: string;
  adminPanelUrl?: string;
  showAdminEntry?: boolean;
  onLogout: () => void;
  onCopyId: () => void;
  idCopied: boolean;
  children: ReactNode;
}

export function getNavTitle(nav: CabinetNav): string {
  return NAV_TITLES[nav];
}

export function CabinetLayout({
  activeNav,
  onNavChange,
  profileTab,
  onProfileTabChange,
  userName,
  userId,
  userTierLabel,
  adminPanelUrl,
  showAdminEntry = false,
  onLogout,
  onCopyId,
  idCopied,
  children,
}: CabinetLayoutProps) {
  const { navPosition } = usePreferences();
  const [profileManualOpen, setProfileManualOpen] = useState<boolean | null>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const profileExpanded = profileManualOpen ?? activeNav === 'profile';

  useEffect(() => {
    if (activeNav !== 'profile') {
      setProfileManualOpen(null);
    }
  }, [activeNav]);

  useEffect(() => {
    if (!userMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!userMenuRef.current?.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey);
    };
  }, [userMenuOpen]);

  const selectNav = (id: CabinetNav) => {
    if (id === 'profile') {
      setProfileManualOpen(true);
      onProfileTabChange('subscription');
    } else {
      setProfileManualOpen(null);
    }
    onNavChange(id);
    window.scrollTo(0, 0);
  };

  const selectProfileTab = (tab: ProfileTab) => {
    setProfileManualOpen(true);
    onProfileTabChange(tab);
    onNavChange('profile');
    window.scrollTo(0, 0);
  };

  const closeUserMenu = () => setUserMenuOpen(false);

  const flatNav = (
    <nav className="cabinet-nav" aria-label="Разделы кабинета">
      {NAV_ITEMS.map(({ id, label, Icon }) => (
        <button
          key={id}
          type="button"
          className={`cabinet-nav-item ${activeNav === id ? 'active' : ''}`}
          onClick={() => selectNav(id)}
        >
          <Icon size={18} />
          <span>{label}</span>
        </button>
      ))}
    </nav>
  );

  const sidebarNav = (
    <nav className="cabinet-nav" aria-label="Разделы кабинета">
      {NAV_ITEMS.map(({ id, label, Icon }) => {
        if (id !== 'profile') {
          return (
            <button
              key={id}
              type="button"
              className={`cabinet-nav-item ${activeNav === id ? 'active' : ''}`}
              onClick={() => selectNav(id)}
            >
              <Icon size={18} />
              <span>{label}</span>
            </button>
          );
        }

        return (
          <div
            key={id}
            className={`cabinet-nav-group${activeNav === 'profile' ? ' is-active' : ''}${profileExpanded ? ' is-open' : ''}`}
          >
            <div className="cabinet-nav-profile-row">
              <button
                type="button"
                className={`cabinet-nav-item cabinet-nav-profile-main ${activeNav === 'profile' ? 'active' : ''}`}
                onClick={() => selectNav('profile')}
                aria-expanded={profileExpanded}
              >
                <Icon size={18} />
                <span>{label}</span>
              </button>
              <button
                type="button"
                className="cabinet-nav-chevron"
                aria-label={profileExpanded ? 'Свернуть профиль' : 'Развернуть профиль'}
                aria-expanded={profileExpanded}
                onClick={() => setProfileManualOpen((v) => !(v ?? activeNav === 'profile'))}
              >
                <IconChevron size={14} />
              </button>
            </div>
            {profileExpanded ? (
              <div className="cabinet-nav-sub" role="group" aria-label="Разделы профиля">
                {PROFILE_TABS.map(({ id: tabId, label: tabLabel, Icon: TabIcon }) => (
                  <button
                    key={tabId}
                    type="button"
                    className={`cabinet-nav-subitem${activeNav === 'profile' && profileTab === tabId ? ' active' : ''}`}
                    onClick={() => selectProfileTab(tabId)}
                  >
                    <TabIcon size={16} />
                    <span>{tabLabel}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </nav>
  );

  return (
    <div className={`cabinet-shell nav-${navPosition}`}>
      <aside className="cabinet-sidebar">
        <div className="cabinet-brand">
          <IconShield size={22} className="brand-icon" />
          <span>Твой провайдер</span>
        </div>
        <p className="cabinet-greeting cabinet-greeting-sidebar">Добро пожаловать, {userName}!</p>

        {sidebarNav}

        <button type="button" className="cabinet-logout" onClick={onLogout}>
          <IconLogout size={18} />
          Выйти
        </button>
      </aside>

      <div className="cabinet-main">
        <header className="cabinet-header">
          <h1 className="cabinet-title">{getNavTitle(activeNav)}</h1>

          <div className="cabinet-user-menu" ref={userMenuRef}>
            <button
              type="button"
              className={`cabinet-user-menu-trigger${userMenuOpen ? ' is-open' : ''}`}
              aria-expanded={userMenuOpen}
              aria-haspopup="menu"
              onClick={() => setUserMenuOpen((v) => !v)}
            >
              <span className="cabinet-user-avatar" aria-hidden>
                <IconUser size={18} />
              </span>
              <span className="cabinet-user-meta">
                <span className="cabinet-user-name">{userName}</span>
                {userTierLabel ? (
                  <span className="cabinet-user-tier">{userTierLabel}</span>
                ) : null}
              </span>
              <IconChevron size={14} />
            </button>

            {userMenuOpen ? (
              <div className="cabinet-user-dropdown" role="menu">
                <div className="cabinet-user-dropdown-label">Telegram ID</div>
                <button
                  type="button"
                  className="cabinet-user-dropdown-id"
                  role="menuitem"
                  onClick={() => {
                    onCopyId();
                  }}
                  title="Скопировать ID"
                >
                  <span className="cabinet-user-dropdown-id-value">{userId}</span>
                  <span className="cabinet-user-dropdown-copy">
                    <IconCopy size={14} />
                    {idCopied ? 'Скопировано' : 'Копировать'}
                  </span>
                </button>

                <div className="cabinet-user-dropdown-divider" />

                <button
                  type="button"
                  className={`cabinet-user-dropdown-item${activeNav === 'settings' ? ' is-active' : ''}`}
                  role="menuitem"
                  onClick={() => {
                    closeUserMenu();
                    selectNav('settings');
                  }}
                >
                  <IconSettings size={16} />
                  <span>Настройки</span>
                </button>

                {showAdminEntry && adminPanelUrl ? (
                  <button
                    type="button"
                    className="cabinet-user-dropdown-item"
                    role="menuitem"
                    title="Только для администраторов"
                    onClick={() => {
                      closeUserMenu();
                      window.location.assign(adminPanelUrl);
                    }}
                  >
                    <IconShield size={16} />
                    <span>Админка</span>
                  </button>
                ) : null}

                <button
                  type="button"
                  className="cabinet-user-dropdown-item cabinet-user-dropdown-logout"
                  role="menuitem"
                  onClick={() => {
                    closeUserMenu();
                    onLogout();
                  }}
                >
                  <IconLogout size={16} />
                  <span>Выйти</span>
                </button>
              </div>
            ) : null}
          </div>
        </header>

        <div className="cabinet-content">{children}</div>

        <footer className="cabinet-footer">
          © {new Date().getFullYear()} Твой провайдер. Все права защищены.
        </footer>
      </div>

      <div className="cabinet-mobile-nav-bar">{flatNav}</div>
    </div>
  );
}
